import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ISiteMapContentItem, ISiteMapContentItemViewModel, ISiteMapTag } from '@fi-shared/models/site-map';
import { takeUntil } from 'rxjs/operators';
import { SiteMapHelperService } from '@fi-shared/fi-shared-services/site-map-helper.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ObjectTypes, SortType } from '@fi-shared/types';
import { ISiteMapItemDropEvent, ISiteMapItemPinnedChangeEvent } from '@fi-app/site-map/site-map';
import { groupBy } from '@fi-app/shared/utils/group-by';
import { isDefined } from '@fi-app/shared/utils/is-defined';

@Component({
    selector: 'fi-site-map-edit-content',
    template: `
        <fi-site-map-table-new
            [siteMapTagData]="siteMapTagData"
            [tagId]="siteMapTagData.ObjectId"
            [items]="siteMapTagChildren$ | async"
            [siteMapViewData]="siteMapViewData$ | async"
            (elementDrop)="onItemDrop($event)"
            (itemPinnedChange)="onItemPinnedChanged($event)"
            [isOriginSitemapTable]="true"
        />
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./site-map-edit-content.component.scss'],
    standalone: false,
})
export class SiteMapEditContentComponent implements OnInit, OnDestroy {
    @Input() siteMapTagData: ISiteMapTag;
    @Input() sortingType: SortType;

    protected siteMapViewData$ = new BehaviorSubject<ISiteMapContentItemViewModel[]>([]);
    protected siteMapTagChildren$ = new BehaviorSubject<ISiteMapContentItem[]>([]);

    constructor(private siteMapHelperService: SiteMapHelperService) {}

    public ngOnInit(): void {
        this.updateSiteMapViewData();
        this.siteMapHelperService.onRecalculateSiteMapItemDepth$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.updateSiteMapViewData();
        });
    }

    private updateSiteMapViewData(): void {
        this.siteMapViewData$.next(
            this.siteMapHelperService.getSiteMapContentItemViewData(
                this.siteMapTagData.ChildItems,
                this.siteMapTagData.Id,
            ),
        );
        this.siteMapTagChildren$.next([...this.siteMapTagData.ChildItems]);
    }

    protected onItemDrop($event: ISiteMapItemDropEvent): void {
        this.siteMapHelperService.setCurrentDraggedSitemapType(undefined);

        const movedItem: ISiteMapContentItem = $event.dropEvent.data;
        let targetTag: ISiteMapContentItem = this.siteMapHelperService.findTagById(
            this.siteMapTagData,
            $event.targetTagId,
        );

        if (movedItem.ParentId === $event.targetTagId) {
            this.moveItemInsideParentTag(targetTag, movedItem, $event.dropEvent.index);
            return;
        }

        const isContentItemAlreadyInTargetList: boolean = targetTag.ChildItems.some(
            (contentItem: ISiteMapContentItem) => contentItem.Id === movedItem.Id,
        );
        if (isContentItemAlreadyInTargetList) {
            this.siteMapHelperService.showItemIsAlreadyTaggedDialog(movedItem, targetTag);
            return;
        }

        const isMovedItemTag = movedItem.ObjectType === ObjectTypes.Tag;
        if (isMovedItemTag && !this.hasTagSubtags(targetTag) && $event.dropEvent.index !== 0) {
            const newParent = this.siteMapHelperService.findTagById(this.siteMapTagData, targetTag.ParentId);
            if (newParent.Id === movedItem.ParentId) {
                const tagGroups = groupBy(newParent.ChildItems, 'ObjectType');
                const movedItemGroup = tagGroups[movedItem.ObjectType];
                const newIndex = movedItemGroup.findIndex((item: ISiteMapContentItem) => item.Id === targetTag.Id) + 1;
                this.moveItemInsideParentTag(newParent, movedItem, newIndex);
                return;
            }

            targetTag = newParent;
        }

        if (!movedItem.HasEditAccess) {
            this.siteMapHelperService.showUserHasNoEditAccessDialog();
            return;
        }

        this.confirmContentItemTagChange(targetTag, movedItem);
    }

    private hasTagSubtags(tag: ISiteMapContentItem): boolean {
        return tag.ChildItems.some((contentItem: ISiteMapContentItem) => contentItem.ObjectType === ObjectTypes.Tag);
    }

    private moveItemInsideParentTag(
        targetTag: ISiteMapContentItem,
        movedItem: ISiteMapContentItem,
        dropIndex: number,
    ): void {
        const tagGroups = groupBy(targetTag.ChildItems, 'ObjectType');
        const movedItemGroup = tagGroups[movedItem.ObjectType];
        const prevIndex = movedItemGroup.findIndex((item: ISiteMapContentItem) => item.Id === movedItem.Id);
        const newIndex = this.siteMapHelperService.normalizeDndIndex(prevIndex, dropIndex);
        moveItemInArray(movedItemGroup, prevIndex, newIndex);
        const newMovedItemIndex: number = movedItemGroup.findIndex(
            (item: ISiteMapContentItem) => item.Id === movedItem.Id,
        );
        if (isDefined(movedItemGroup[newMovedItemIndex])) {
            movedItemGroup[newMovedItemIndex].IsPinned = true;
        }
        targetTag.ChildItems = [
            ...(tagGroups[ObjectTypes.Document] || []),
            ...(tagGroups[ObjectTypes.MediaBank] || []),
            ...(tagGroups[ObjectTypes.Tag] || []),
        ];
        this.updateSiteMapViewData();
        this.siteMapHelperService.triggerSetTagsDataSets(targetTag.Id);
    }

    private confirmContentItemTagChange(targetTag: ISiteMapContentItem, movedItem: ISiteMapContentItem): void {
        this.siteMapHelperService
            .confirmContentItemTagChange(this.siteMapTagData, targetTag, movedItem)
            .onYesButtonClick(() => {
                movedItem.ParentId = targetTag.Id;
                movedItem.Level = targetTag.Level + 1;
                targetTag.ChildItems.push(movedItem);
                this.siteMapHelperService.removeDroppedItemFromOldParent(movedItem, this.siteMapTagData);
                targetTag.ChildItems = this.siteMapHelperService.getSortedTagData(
                    targetTag.ChildItems,
                    this.sortingType,
                );
                this.updateSiteMapViewData();
            });
    }

    protected onItemPinnedChanged($event: ISiteMapItemPinnedChangeEvent): void {
        if (!$event.item.IsPinned) {
            // item unpinned, restore initial sort
            const targetTag: ISiteMapContentItem = this.siteMapHelperService.findTagById(
                this.siteMapTagData,
                $event.targetTagId,
            );
            targetTag.ChildItems = this.siteMapHelperService.getSortedTagData(targetTag.ChildItems, this.sortingType);
            this.updateSiteMapViewData();
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
